import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { Investor } from "../../api";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";
import "./investorCombobox.css";

const MAX_RESULTS = 50;

// Type-to-search investor picker for the Embedded Flow launcher. Filters the
// already-loaded list client-side on name, email, city and org number, so it
// stays instant for the demo book. Keyboard: ↑/↓ to move, Enter to pick,
// Escape to close.
export function InvestorCombobox({
  investors,
  value,
  onChange,
}: {
  investors: Investor[] | null;
  value: number | "";
  onChange: (id: number | "") => void;
}) {
  const selected = useMemo(
    () => (value === "" ? null : (investors ?? []).find((i) => i.id === value) ?? null),
    [investors, value],
  );
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Show the chosen investor's name in the box whenever the list is closed.
  useEffect(() => {
    if (!open) setQuery(selected?.name ?? "");
  }, [selected, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const matches = useMemo(() => {
    const all = investors ?? [];
    const q = query.trim().toLowerCase();
    // While the box still shows the selected name, list everyone.
    if (!q || (selected && query === selected.name)) return all.slice(0, MAX_RESULTS);
    return all
      .filter((i) =>
        [i.name, i.email, i.city, i.org_id].some((f) => f?.toLowerCase().includes(q)),
      )
      .slice(0, MAX_RESULTS);
  }, [investors, query, selected]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function pick(i: Investor) {
    onChange(i.id);
    setQuery(i.name);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) return setOpen(true);
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (open && matches[active]) {
        e.preventDefault();
        pick(matches[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="investor-combo" ref={rootRef}>
      <input
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="investor-combo-list"
        aria-autocomplete="list"
        placeholder={investors ? "Search by name, email, city or org number…" : "Loading investors…"}
        disabled={!investors}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (e.target.value === "") onChange("");
        }}
        onFocus={(e) => {
          e.target.select();
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {open && investors && (
        <ul className="investor-combo-list" id="investor-combo-list" role="listbox" ref={listRef}>
          {matches.length === 0 ? (
            <li className="investor-combo-empty">No investors match “{query}”.</li>
          ) : (
            matches.map((i, idx) => (
              <li
                key={i.id}
                data-index={idx}
                role="option"
                aria-selected={i.id === value}
                className={
                  "investor-combo-option" +
                  (idx === active ? " is-active" : "") +
                  (i.id === value ? " is-selected" : "")
                }
                onMouseEnter={() => setActive(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i);
                }}
              >
                <ClientTypeIcon type={i.type} className="investor-combo-icon" />
                <div className="investor-combo-text">
                  <div className="investor-combo-name">{i.name}</div>
                  <div className="investor-combo-meta">
                    {[i.email, i.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                {i.qp_client_id && <div className="investor-combo-linked">Linked</div>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
